import { computed, effectScope, ref, shallowRef } from 'vue';

import { useAdConsent, type AdConsentApi } from '~/composables/useAdConsent';
import {
   adConsentAudience,
   adTrackingAllowed,
   canAskAdConsent,
   canReadJourneyConsent,
   hasAdChoice,
   type AdConsentAudience,
   type AdConsentPromptContext,
} from '~/utils/adConsentPrompt';

export type AdConsentPromptOptions = {
   // The signed-out visitor's answer, stored on their anonymous journey. Signup
   // copies it to the new account, so the client is not asked a second time.
   journeyApi?: AdConsentApi;
   // Called with whether ad measurement may run for this visitor. It never gets `true`
   // without an explicit yes that the server also allows, and a failed save or
   // load is `false`. Only signed-out visitors go through it: a signed-in client's
   // measurement is decided elsewhere.
   onTracking?: (allowed: boolean) => void;
};

// Decides when the consent question is shown and records the answer. It never
// loads or runs any tracker itself: its only side effects are the GET and PUT in
// `api` / `journeyApi` and the `onTracking` hook, which the caller wires to the
// one place that decides whether anything loads.
//
// The question is put at most once per page load, and only when the server says
// to (`should_prompt === true`). Every failure on the way in (route, network,
// older API) means silence, never an error for the client.
export const createAdConsentPrompt = (
   api: AdConsentApi,
   options: AdConsentPromptOptions = {},
) => {
   // One consent state per signed-in client, so one client's answer from the
   // server can never open the popup for the next.
   // Each client's state lives in its own scope so the previous one's watchers
   // stop when the client changes.
   let scope = effectScope(true);
   let audience: AdConsentAudience = 'client';
   const apiFor = () => (audience === 'journey' && options.journeyApi ? options.journeyApi : api);
   const create = () => scope.run(() => useAdConsent(apiFor()))!;
   const decide = (allowed: boolean) => {
      if (audience === 'journey') options.onTracking?.(allowed);
   };
   const current = shallowRef(create());
   const open = ref(false);
   // 'intro' is the first screen; 'settings' is the same dialog showing the choice
   // switch. The switch always starts off, so a client who opens settings, backs
   // out and returns is never carried along by an earlier toggle.
   const view = ref<'intro' | 'settings'>('intro');
   const choice = ref(false);
   let generation = 0;
   let account: string | null = null;
   let requested = false;
   let prompted = false;
   let askedByServer = false;
   let last: AdConsentPromptContext | null = null;

   const saving = computed(() => current.value.saving.value);
   const saveFailed = computed(() => current.value.saveFailed.value);
   // Closing without an answer is only offered once a save has failed, so the
   // client can never be stuck behind a broken connection.
   const canDismiss = computed(() => saveFailed.value && !saving.value);

   const reset = () => {
      generation++;
      scope.stop();
      scope = effectScope(true);
      current.value = create();
      open.value = false;
      view.value = 'intro';
      choice.value = false;
      requested = false;
      prompted = false;
      askedByServer = false;
   };

   const evaluate = () => {
      const ctx = last;
      if (!ctx) return;
      if (!canAskAdConsent(ctx)) {
         open.value = false;
         view.value = 'intro';
         choice.value = false;
         if (!requested && canReadJourneyConsent(ctx)) {
            requested = true;
            void request();
         }
         return;
      }
      if (open.value || prompted) return;
      if (!requested) {
         requested = true;
         void request();
         return;
      }
      if (askedByServer && !ctx.isBlocked()) {
         prompted = true;
         open.value = true;
      }
   };

   const request = async () => {
      const mine = generation;
      const state = current.value;
      await state.load();
      if (mine !== generation) return;
      const answered = state.loaded.value && !state.loadFailed.value;
      askedByServer = answered && state.shouldPrompt.value;
      if (audience === 'journey') {
         const consent = state.consent.value;
         // An answer already on the journey (a yes or no from the landing page) is
         // respected without asking. Anything unreadable is silence and no tracking.
         if (answered && hasAdChoice(consent)) askedByServer = false;
         if (!askedByServer) decide(answered && adTrackingAllowed(consent));
      }
      evaluate();
   };

   const sync = (ctx: AdConsentPromptContext) => {
      // Signing up turns the visitor into a client: the visitor's state is dropped
      // and the client's own answer, which signup carried over, is read afresh.
      const next = adConsentAudience(ctx);
      if (next !== audience) {
         audience = next;
         reset();
      }
      const key = ctx.authenticated ? ctx.accountKey : (ctx.journeyKey ?? null);
      if (key && account && key !== account) reset();
      if (key) account = key;
      last = ctx;
      evaluate();
   };

   // Agree and Confirm both save through the API. A successful save ends the
   // question for this page load whatever `should_prompt` comes back as.
   const answer = async (granted: boolean) => {
      const state = current.value;
      const mine = generation;
      if (!open.value || state.saving.value) return;
      const saved = await state.setGranted(granted);
      if (mine !== generation) return;
      decide(saved && adTrackingAllowed(state.consent.value));
      if (saved) open.value = false;
   };

   const dismiss = () => {
      if (canDismiss.value) open.value = false;
   };

   const manage = () => {
      if (!open.value || saving.value) return;
      choice.value = false;
      view.value = 'settings';
   };

   // Leaving the settings view records nothing.
   const back = () => {
      if (!open.value || saving.value) return;
      view.value = 'intro';
      choice.value = false;
   };

   const confirm = () => answer(choice.value);

   // What X and Escape do: step back out of settings, or, on the first screen,
   // dismiss only once a failed save has made that possible. Neither ever saves.
   const close = () => {
      if (view.value === 'settings') back();
      else dismiss();
   };

   return {
      open,
      view,
      choice,
      saving,
      saveFailed,
      canDismiss,
      sync,
      answer,
      dismiss,
      manage,
      back,
      confirm,
      close,
   };
};

let shared: ReturnType<typeof createAdConsentPrompt> | null = null;

// One instance per page load: "at most once" has to survive the component being
// remounted by a language switch or a layout change.
export const useAdConsentPrompt = () => {
   if (!shared) {
      const { getAdConsent, setAdConsent } = useClientAccountSettingsStore();
      const { getJourneyAdConsent, setJourneyAdConsent } = useClientAuthStore();
      shared = createAdConsentPrompt(
         {
            fetchConsent: () => getAdConsent('client-ad-consent-prompt-get'),
            saveConsent: (granted) => setAdConsent(granted, 'client-ad-consent-prompt-put'),
         },
         {
            journeyApi: {
               fetchConsent: () => getJourneyAdConsent(),
               saveConsent: (granted) => setJourneyAdConsent(granted),
            },
            onTracking: (allowed) => setPixelAllowed(allowed),
         },
      );
   }
   return shared;
};
